import { useCallback, useState, useEffect } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { cookieJarConstants } from 'dibs-cookie-jar';
import { getBuyerId } from 'dibs-cookie-jar';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
import { sessionStorage } from 'dibs-browser-storage';

import { LIKED_PHOTO_STORAGE_KEY } from './constants.ts';
import { addPhotoLikeMutation } from './mutations/addPhotoLikeMutation.ts';
import { removePhotoLikeMutation } from './mutations/removePhotoLikeMutation.ts';
import {
    optimisticPhotoLikeUpdater,
    addPhotoLikeUpdater,
    removePhotoLikeUpdater,
} from './mutations/photoLikeUpdaters.ts';
import { type PhotoLikeProps } from './types.ts';
import { trackLoginSavePhotoFeature } from '../helpers/trackingHelpers.ts';

const { LIKE_PHOTO_FLOW } = authModalLoader.constants;
const { USER_EMAIL } = cookieJarConstants;

type UseHandlePhotoLikeReturnType = {
    isLiked: boolean;
    isAnimated: boolean;
    handlePhotoLike: () => Promise<void>;
};

type UseHandlePhotoLikeInput = {
    showFeedback?: () => void;
} & PhotoLikeProps;

const handleUserLogin = async (): Promise<string> => {
    trackLoginSavePhotoFeature({ action: 'entry' });
    const result = await authModalLoader.show({
        email: localStorage.getItem(USER_EMAIL),
        flow: LIKE_PHOTO_FLOW,
    });
    const userId: string = result?.userId || '';
    if (userId) {
        trackLoginSavePhotoFeature({ action: 'complete' });
    }
    return userId;
};

export const useHandlePhotoLike = ({
    photoLikeId,
    photoId,
    photoNodeId,
    onPhotoLikeAdded,
    showFeedback,
}: UseHandlePhotoLikeInput): UseHandlePhotoLikeReturnType => {
    const environment = useRelayEnvironment();
    const [isLiked, setIsLiked] = useState(!!photoLikeId);
    const [isAnimated, setIsAnimated] = useState(false);

    useEffect(() => {
        setIsLiked(!!photoLikeId);
    }, [photoLikeId, photoId]);

    const handleRemovePhotoLike = useCallback(
        async (userId: string) => {
            await removePhotoLikeMutation({
                environment,
                input: { photoLikeId },
                optimisticUpdater: store => {
                    optimisticPhotoLikeUpdater({
                        store,
                        optimisticResult: false,
                        photoNodeId,
                        userId,
                    });
                },
                updater: store => removePhotoLikeUpdater({ store, photoNodeId, userId }),
            });
        },
        [environment, photoNodeId, photoLikeId]
    );

    const handleAddPhotoLike = useCallback(
        async (userId: string) => {
            const result = await addPhotoLikeMutation({
                environment,
                input: {
                    userId,
                    photoId: Number(photoId),
                },
                optimisticUpdater: store => {
                    optimisticPhotoLikeUpdater({
                        store,
                        optimisticResult: true,
                        photoNodeId,
                        userId,
                    });
                },
                updater: store => addPhotoLikeUpdater({ store, photoNodeId, userId }),
            });
            const mutationPhotoLikeId = result.addPhotoLikeMutation?.photoLikeId;
            if (mutationPhotoLikeId) {
                const userEverLikedPhoto = !!sessionStorage.getItem(LIKED_PHOTO_STORAGE_KEY);
                if (showFeedback && !userEverLikedPhoto) {
                    showFeedback();
                    sessionStorage.setItem(LIKED_PHOTO_STORAGE_KEY, true);
                }
                if (onPhotoLikeAdded) {
                    onPhotoLikeAdded();
                }
            }
        },
        [environment, photoNodeId, photoId, onPhotoLikeAdded, showFeedback]
    );

    const handlePhotoLike = useCallback(async () => {
        let userId = getBuyerId(document.cookie);
        if (!userId) {
            userId = await handleUserLogin();
        }
        if (userId && photoId) {
            try {
                setIsAnimated(true);
                setIsLiked(prevIsLiked => !prevIsLiked);
                if (photoLikeId) {
                    await handleRemovePhotoLike(userId);
                } else {
                    await handleAddPhotoLike(userId);
                }
            } catch (e) {
                setIsLiked(prevIsLiked => !prevIsLiked);
            }
            setIsAnimated(false);
        }
    }, [photoId, photoLikeId, handleRemovePhotoLike, handleAddPhotoLike]);

    return {
        isLiked,
        isAnimated,
        handlePhotoLike,
    };
};
